import { toast } from 'sonner';
import api from '@/utils/api';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateDetails(f) {
  const e = {};
  if (f.account_type === 'organization' && !f.company_name.trim()) e.company_name = 'Company name is required';
  if (!f.full_name.trim()) e.full_name = 'Your full name is required';
  return e;
}

const emailError = (email) => {
  if (!email.trim()) return 'Email address is required';
  return EMAIL_RE.test(email.trim()) ? null : 'Enter a valid email address, e.g. you@company.com';
};

const passwordError = (pw) => {
  if (!pw) return 'Password is required';
  return pw.length < 8 ? `Password must be at least 8 characters (${pw.length}/8)` : null;
};

const confirmError = (pw, confirm) => {
  if (!confirm) return 'Please re-enter your password';
  return pw !== confirm ? 'Passwords do not match' : null;
};

export function validateCredentials(f) {
  const e = {
    email: emailError(f.email),
    password: passwordError(f.password),
    confirmPassword: confirmError(f.password, f.confirmPassword),
  };
  Object.keys(e).forEach((k) => { if (!e[k]) delete e[k]; });
  return e;
}

export function buildSignupPayload(f, notifications) {
  const isOrg = f.account_type === 'organization';
  return {
    account_type: f.account_type,
    selected_tier: isOrg ? f.selected_tier : 'freelancer',
    units: f.units || 1,
    additional_users: isOrg ? (f.additional_users || 0) : 0,
    payment_option: f.payment_option || 'trial',
    company_name: isOrg ? f.company_name.trim() : f.full_name.trim(),
    full_name: f.full_name.trim(),
    email: f.email.trim().toLowerCase(),
    password: f.password,
    contact_number: f.contact_number.trim(),
    referral_code: f.referral_code || undefined,
    notification_preferences: isOrg ? notifications : undefined,
  };
}

export function warnEmailDelivery(sendStatus) {
  if (sendStatus === 'quota_exceeded') {
    toast.warning('Email service is rate-limited today — your verification email may be delayed. Contact admin@safer-i.com if it doesn\'t arrive within an hour.', { duration: 8000 });
  } else if (sendStatus && sendStatus !== 'delivered' && sendStatus !== 'config_missing') {
    toast.warning('Verification email may not have been delivered. You can request another from the banner once you log in.', { duration: 6000 });
  }
}

// Returns { field, message }: field is 'email' when the error belongs inline on the email input.
export function parseSignupError(error) {
  const detail = error.response?.data?.detail;
  let message = 'Signup failed. Please try again.';
  if (typeof detail === 'string') message = detail;
  else if (Array.isArray(detail)) message = detail.map((d) => d.msg || d.message || JSON.stringify(d)).join('. ');
  if (!/email/i.test(message)) return { field: null, message };
  if (/already registered/i.test(message)) {
    return { field: 'email', message: 'This email already has an account. Sign in instead, or use a different email.' };
  }
  return { field: 'email', message };
}

// Pay-now orgs go to Stripe checkout; returns the fallback route when checkout can't start.
export async function startOrgCheckout(f) {
  try {
    toast.info('Redirecting to secure payment...');
    const { data } = await api.post('/stripe/tier/checkout', {
      selected_tier: f.selected_tier,
      units: f.units || 1,
      additional_users: f.additional_users || 0,
      origin_url: window.location.origin,
    });
    if (data?.checkout_url) {
      window.location.href = data.checkout_url;
      return null;
    }
    toast.error('Unable to start checkout. Redirecting to billing.');
  } catch (err) {
    const msg = err.response?.data?.detail;
    toast.error(typeof msg === 'string' ? msg : 'Payment session failed. You can retry from the Billing page.');
  }
  return '/billing';
}
